export type SurveyElementType =
  | 'text'
  | 'dropdown'
  | 'choices'
  | 'file-uploader'
  | 'datepicker'
  | 'time-picker'
  | 'regions'
  | 'budget'
  | 'alert'
  | 'location-type'
  | 'location-picker'
  | 'price-modifier'
export type SurveyElementConditionType =
  | 'equalTo'
  | 'contains'
  | 'notEqual'
  | 'notContains'
  | 'anyOf'
  | 'empty'
  | 'notEmpty'
export type SurveyElementValidatorType = 'email' | 'phone'
export type SurveyPageProcessorCommand =
  | 'log'
  | 'test'
  | 'template'
  | 'alert'
  | 'get-customer-address'
export type SurveyValueType =
  | string
  | number
  | string[]
  | number[]
  | undefined
  | object
export type SurveyChoiceOptionType = {
  value: string | number
  label: SurveyLocalizedString
}
export type SurveyPriceModifierChoiceOptionType = SurveyChoiceOptionType & {
  modifier: number
}

export interface SurveyAnswers {
  [pageName: string]: {
    [elementName: string]: {
      value: SurveyValueType
      otherValue: SurveyValueType
    }
  }
}

export interface SurveyAnswersSimple {
  [pageName: string]: {
    [elementName: string]: SurveyValueType
  }
}

export interface SurveyL10n {
  [lang: string]: {
    [pageName: string]: {
      [elementName: string]: {
        title?: string
        errorText?: string
        placeholder?: string
        hint?: string
        choices?: {
          [value: string]: string
        }
      }
    }
  }
}

export interface SurveyData {
  _id?: string
  name: string
  model: Survey
  templates?: SurveyData[] | null
  l10n?: SurveyL10n | null
  isTemplate?: boolean
  isStartingTemplate?: boolean
  dateCreated: number
  dateUpdated?: number | null
  createdBy?: string | null
  rev?: number
}

export interface SurveyDraftTranslations {
  [language: string]: {
    isCompleted: boolean
    inProgress: boolean
    dateUpdated?: number | null
    changes?: string[] // list of changed field `pageName.elementName.choices.value1` | `pageName.elementName.title`
  }
}

export interface SurveyDraftData {
  _id?: string
  name: string
  questionnaire: SurveyData
  dateCreated: number
  dateUpdated?: number | null
  datePublished?: number | null
  createdBy?: string | null
  note?: string | null
  readyForTranslation?: boolean | null
  dateReadyForTranslation?: number | null
  status?: 'DRAFT' | 'PUBLISHED' | string
  translations?: SurveyDraftTranslations
}

export interface Survey {
  name: string
  pages: SurveyPage[]
  autoAdvance?: boolean
  isTemplate?: boolean
  isStartingTemplate?: boolean
  version?: string
}

export interface SurveyPage {
  name: string
  elements: SurveyElement[]
  preProcessor?: SurveyPageProcessorCommand
  postProcessor?: SurveyPageProcessorCommand
  branches?: SurveyBranch[]
}

export type SurveyTargetPageType = 'COMPLETE' | 'TEMPLATE' | string

export interface SurveyBranch {
  targetPage: SurveyTargetPageType
  conditions?: SurveyBranchCondition[]
}

export interface SurveyBranchCondition {
  elementName: string
  value: string
  conditionChain: 'or' | 'and'
}

export interface SurveyLocalizedString {
  [lang: string]: string
}

export interface SurveyElementCondition {
  type: SurveyElementConditionType
  value: string | number
  target: {
    page: string
    element: string
  }
}

export interface SurveyElementValidator {
  type: SurveyElementValidatorType
  arguments?: unknown
}

export interface SurveyElementBase {
  name: string
  type: SurveyElementType
  title: SurveyLocalizedString
  hint?: SurveyLocalizedString
  errorText?: SurveyLocalizedString
  required?: boolean
  condition?: SurveyElementCondition
  validation?: SurveyElementValidator
  value?: SurveyValueType
  // For filtering in backend
  system?: boolean
  // For visualization in frontend
  priority?: boolean
  prefilled?: boolean // is the question answered from whatApp bot
}

export interface SurveyElementText extends SurveyElementBase {
  value?: string
  placeholder?: SurveyLocalizedString
}

export interface SurveyElementDropdown extends SurveyElementBase {
  placeholder?: SurveyLocalizedString
  choices?: SurveyChoiceOptionType[]
  multiple?: boolean
  searchable?: boolean
}

export interface SurveyElementDatepicker extends SurveyElementBase {
  multiple?: boolean
}

export interface SurveyElementChoices extends SurveyElementBase {
  choices?: SurveyChoiceOptionType[]
  multiple?: boolean
  hasOther?: boolean
  otherValue?: SurveyValueType
}

export interface SurveyElementPriceModifier extends SurveyElementBase {
  choices?: SurveyPriceModifierChoiceOptionType[]
  multiple?: boolean
}

export interface SurveyElementFileUploader extends SurveyElementBase {
  value?: string
  accept?: 'images' | 'docs' | 'images,docs'
}

export type SurveyElementTimePickerRange = [number, number]

export interface SurveyElementTimePicker extends SurveyElementBase {
  ranges: SurveyElementTimePickerRange[]
  hasOther?: boolean
  otherValue?: SurveyElementTimePickerRange
  value?: SurveyElementTimePickerRange
}

export interface SurveyElementRegions extends SurveyElementBase {
  multiple?: boolean
}

export interface SurveyElementBudget extends SurveyElementBase {
  value?: number
}

export interface SurveyElementAlert extends SurveyElementBase {
  severity?: 'success' | 'info' | 'warning' | 'error'
  header?: string
  body?: string
}

export interface SurveyElementLocationType
  extends SurveyElementBase,
    SurveyElementWhatsApp {
  value?: 'location' | 'office' | 'online'
  // bit mask for available options
  mask: number
}

export interface SurveyElementWhatsApp {
  whatsapp?: boolean // is the question available for whatsapp ChatBot
}

export interface SurveyElementLocationPickerValue {
  lat: number
  lon: number
  address: string
  regions: string[]
  range?: number
}

export interface SurveyElementLocationPicker
  extends SurveyElementBase,
    SurveyElementWhatsApp {
  value?: SurveyElementLocationPickerValue
  variant: 'location' | 'range'
}

export type SurveyElement =
  | SurveyElementText
  | SurveyElementDropdown
  | SurveyElementDatepicker
  | SurveyElementChoices
  | SurveyElementPriceModifier
  | SurveyElementFileUploader
  | SurveyElementTimePicker
  | SurveyElementRegions
  | SurveyElementBudget
  | SurveyElementAlert
  | SurveyElementLocationType
  | SurveyElementLocationPicker

export class SurveyObject {
  survey!: Survey
  name!: string
  templates?: SurveyData[] | null
  isTemplate?: boolean
  isStartingTemplate?: boolean
  status?: SurveyDraftData['status']

  _id?: string
  type!: 'draft' | 'publish'

  draft?: {
    _id?: string
    dateCreated?: number
    dateUpdated?: number | null
    datePublished?: number | null
    readyForTranslation?: boolean
    dateReadyForTranslation?: number
    translations?: SurveyDraftTranslations
  }

  publish?: {
    _id?: string
    dateCreated?: number
    dateUpdated?: number
  }

  l10n?: SurveyL10n | null

  fromDraft(draft: SurveyDraftData) {
    this.fromPublish(draft.questionnaire)
    this.draft = {
      _id: draft._id,
      dateCreated: draft.dateCreated,
      dateUpdated: draft.dateUpdated,
      datePublished: draft.datePublished,
      readyForTranslation: !!draft.readyForTranslation,
      dateReadyForTranslation: draft.dateReadyForTranslation ?? undefined,
      translations: draft.translations
    }
    this._id = draft._id
    this.type = 'draft'
    this.status = draft.status
    return this
  }

  fromPublish(publish: SurveyData) {
    this.survey = publish.model
    this.templates = publish.templates
    this.name = publish.name
    this.isTemplate = publish.isTemplate
    this.isStartingTemplate = publish.isStartingTemplate
    this.publish = {
      _id: publish._id,
      dateCreated: publish.dateCreated
    }
    this._id = publish._id
    this.type = 'publish'
    this.l10n = publish.l10n
    return this
  }

  fromData(
    data: SurveyData,
    type: 'publish' | 'draft',
    draftData?: SurveyDraftData
  ) {
    this.survey = data.model
    this.templates = data.templates
    this.name = data.name
    this.isTemplate = data.isTemplate
    this.isStartingTemplate = data.isStartingTemplate
    this._id = data._id
    this.type = type
    this.l10n = data.l10n
    this[type] = {
      _id: data._id,
      dateCreated: data.dateCreated,
      readyForTranslation: draftData?.readyForTranslation ?? false,
      dateReadyForTranslation: draftData?.dateReadyForTranslation ?? undefined,
      translations: draftData?.translations
    }
    return this
  }

  updateSurvey(survey: Survey) {
    this.survey = survey
    this.name = survey.name
    this.isTemplate = survey.isTemplate
    this.isStartingTemplate = survey.isStartingTemplate
  }

  updateTemplates(templates?: SurveyData[] | null) {
    this.templates = templates
  }

  clone(): SurveyObject {
    return structuredClone(this)
  }

  exportSurveyData(): SurveyData {
    return {
      name: this.name,
      model: this.survey,
      templates: this.templates,
      isTemplate: this.survey.isTemplate,
      isStartingTemplate: this.survey.isStartingTemplate,
      _id: this.publish!._id!,
      dateCreated: this.publish!.dateCreated!
    }
  }

  exportTemplate(): SurveyData {
    return {
      name: this.name,
      model: this.survey,
      templates: this.templates,
      isTemplate: this.survey.isTemplate,
      isStartingTemplate: this.survey.isStartingTemplate,
      _id: this.publish!._id!,
      dateCreated: this.publish!.dateCreated!,
      l10n: this.l10n
    }
  }
}
